import { gql } from '@apollo/client';
import { executeMutation } from 'libs/executeMutation';
import { reportError } from '@xbcb/ui-utils';
import {
  UsHts,
  UpdateUsHtsInput,
  AdditionalHtsInput,
  AdditionalHtsType,
} from '__generated__/graphql';

export interface UpdateResult {
  success: boolean;
  updatedCount: number;
  errors: string[];
}

const UPDATE_US_HTS_MUTATION = gql`
  mutation updateUsHts($id: ID!, $input: UpdateUsHtsInput!, $version: Int!) {
    updateUsHts(id: $id, input: $input, version: $version) {
      __typename
    }
  }
`;

/**
 * Updates HTS records by adding additional tariffs to the additionalHtssApplicable field
 * @param {UsHts[]} htsRecords - Array of HTS records to update
 * @param {string} additionalTariff - The additional tariff to apply to all records
 * @param {object} additionalFields - Additional fields to update (type, startDate, description, exportGroup)
 * @return {Promise<UpdateResult>} Promise resolving to the update result with success status and counts
 */
export const updateHtsRecordsWithAdditionalTariff = async (
  htsRecords: UsHts[],
  additionalTariff: string,
  additionalFields?: {
    type?: string;
    startDate?: string;
    description?: string;
    exportGroup?: string;
  },
): Promise<UpdateResult> => {
  const result: UpdateResult = {
    success: true,
    updatedCount: 0,
    errors: [],
  };

  try {
    for (const htsRecord of htsRecords) {
      try {
        await updateHtsRecord(htsRecord, [additionalTariff], additionalFields);
        result.updatedCount++;
      } catch (error) {
        const errorMsg = `Failed to update HTS ${htsRecord.number}: ${
          error instanceof Error ? error.message : String(error)
        }`;
        result.errors.push(errorMsg);
        reportError(new Error(errorMsg));
      }
    }
  } catch (error) {
    result.success = false;
    result.errors.push(
      `General error: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }

  return result;
};

/**
 * Update a single HTS record with additional tariffs
 * @param {UsHts} htsRecord - The HTS record to update
 * @param {string[]} additionalTariffs - Array of additional tariff strings to add
 * @param {object} additionalFields - Additional fields to update
 * @return {Promise<void>} Promise that resolves when the update is complete
 */
async function updateHtsRecord(
  htsRecord: UsHts,
  additionalTariffs: string[],
  additionalFields?: {
    type?: string;
    startDate?: string;
    description?: string;
    exportGroup?: string;
  },
): Promise<void> {
  try {
    // Convert existing tariffs to input format
    const existingTariffsInput: AdditionalHtsInput[] = (
      htsRecord.additionalHtssApplicable || []
    ).map((existing) => ({
      number: existing.number,
      type: existing.type || undefined,
      startDate: existing.startDate || undefined,
      additionalTypeDescription:
        existing.additionalTypeDescription || undefined,
      exportGroup: existing.exportGroup || undefined,
    }));

    // Format new tariffs as objects with number property and additional fields
    const formattedNewTariffs: AdditionalHtsInput[] = additionalTariffs.map(
      (tariff) => ({
        number: tariff,
        type: additionalFields?.type as AdditionalHtsType,
        startDate: additionalFields?.startDate,
        additionalTypeDescription: additionalFields?.description,
        exportGroup: additionalFields?.exportGroup,
      }),
    );

    // Combine existing and new tariffs
    const allTariffs = [...existingTariffsInput, ...formattedNewTariffs];

    const updateInput: UpdateUsHtsInput = {
      additionalHtssApplicable: allTariffs,
    };

    const mutationVariables = {
      id: htsRecord.id,
      input: updateInput,
      version: htsRecord.version,
    };

    await executeMutation({
      mutation: UPDATE_US_HTS_MUTATION,
      variables: mutationVariables,
      successMessage: '',
      shouldThrowError: true,
    });
  } catch (error) {
    reportError(error instanceof Error ? error : new Error(String(error)));
    throw new Error(
      `Failed to update HTS record ${htsRecord.number}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}
